import * as THREE from "three";
import { SeeThrough } from "./seeThrough";
import { CloudShadows } from "./cloudShadows";
import { VfxLights } from "./vfxLights";
import { StrikeLight } from "./strikeLight";
import { FlagEmblem } from "./flagEmblem";

/**
 * Materials for the HQ set (art/SHADING.md §2). The GLTFLoader's MeshStandardMaterial — albedo, tangent normal map,
 * roughness/metalness, AO — is kept as is; what is added: the `_glow` slot renders unlit and HDR from its emissive map
 * (it feeds the bloom pass, and breathes; see `glowMaterial`), ground/prop materials whose meshes carry `COLOR_0` wind
 * weights get the per-vertex wind from art/hq/README.md ("Ground kit v4"), and every lit material gets the shared
 * lighting extras (`addLitExtras`: baked AO on direct light, cloud shadows, the strike-light slot, a tower rim).
 */
export class HqMaterials {
  /** Seconds, shared by every wind material; the level/hub loop advances it. */
  static readonly time = { value: 0 };
  /** Seconds for the chest's light shaft; the ChestStage advances it. */
  static readonly beamTime = { value: 0 };
  /** Ground macro-variation (render/look.ts): strength and the two tints tiles drift toward by world-space noise. */
  static readonly groundVar = { value: 0 };
  static readonly groundWarm = { value: new THREE.Color(0xb8b84a) };
  static readonly groundCool = { value: new THREE.Color(0x2f7a52) };
  /** Every converted PBR material, so a look change can re-set IBL strength and normal depth on all of them. */
  static readonly standard: { mat: THREE.MeshStandardMaterial; ground: boolean }[] = [];
  /**
   * The look's IBL strength, kept so materials converted AFTER the look was applied get it too.
   *
   * `convert` used to set every material's `envMapIntensity` to 0 and rely on `applyLook` to raise it — but the rig
   * applies its look in its constructor, before the asset library has loaded a single model, so in the shipped game
   * every HQ material rendered with no image-based light at all (metals flat, no sky in the glossy paint). Only a live
   * `setLook` from the debug hook ever lit them. Now `convert` reads the current value.
   */
  private static envNow = 0;
  /**
   * Ground pieces (`g_*` tiles, bridges, ramps) take a third of the look's IBL (lighting review F1: the blue PMREM at
   * full strength on a roughness-1 surface is a blue diffuse fill on the grass). Towers, props and units keep it all.
   */
  static readonly GroundEnvScale = 1 / 3;
  static applyLook(envIntensity: number, normalScale: number): void {
    HqMaterials.envNow = envIntensity;
    for (const { mat, ground } of HqMaterials.standard) {
      mat.envMapIntensity = ground ? envIntensity * HqMaterials.GroundEnvScale : envIntensity;
      if (mat.normalMap) mat.normalScale.set(ground ? normalScale : Math.max(1, normalScale * 0.9), ground ? normalScale : Math.max(1, normalScale * 0.9));
      mat.needsUpdate = true;
    }
  }
  /**
   * What may sway in the wind.
   *
   * This used to be "any `g_`/`p_` mesh that carries a COLOR_0 attribute", but every art kit bakes COLOR_0 on every
   * model — so boulders, logs, stumps, mesas, basalt columns and bone piles all swayed like grass. Wind is a property
   * of the thing, not of whether the exporter happened to write a colour attribute, so it is named here.
   */
  static readonly Sways = /^(p_tree_|p_bush|p_grass|p_flowers|p_reeds|p_lilypads|p_cypress|p_dead_willow|p_ash_tree|d_tuft|d_flowers|d_clover|d_fern|d_reed|d_moss|d_ash_tuft|d_marsh_mushroom|d_cards|g_grass)/;
  /**
   * Leafy models that get the foliage light (`addFoliageLight`): the Phase 3 lobe canopies, the bushes and the detail
   * card clumps. Gated per vertex by COLOR_0.b, which the Phase 3 trees set to 0 on bark.
   */
  static readonly Foliage = /^(p_tree_|p_cypress|p_dead_willow|p_bush|d_cards)/;
  private readonly cache = new Map<string, THREE.Material>();

  /**
   * A copy of a converted material that compiles to its own program: same maps and shader hooks (wind, haze, ground
   * variation — `Material.clone` drops them), and it follows look changes like the original. For geometry that draws
   * the same surface through a different vertex path (baked scenery next to skinned rigs, see rigidRig.ts): one
   * material shared by both makes three.js re-derive the program on every switch between them.
   */
  static twin(source: THREE.Material): THREE.Material {
    let t = HqMaterials.twins.get(source);
    if (t) return t;
    t = source.clone();
    t.onBeforeCompile = source.onBeforeCompile;
    t.customProgramCacheKey = source.customProgramCacheKey;
    const entry = HqMaterials.standard.find((e) => e.mat === source);
    if (entry) HqMaterials.standard.push({ mat: t as THREE.MeshStandardMaterial, ground: entry.ground });
    HqMaterials.twins.set(source, t);
    return t;
  }
  private static readonly twins = new WeakMap<THREE.Material, THREE.Material>();

  /**
   * Materials other modules derive from converted ones (skin variants, render/skins/skinMaterial.ts): each hook gets
   * the materials being forgotten and returns its derived copies, which `forget` drops from the look list and disposes.
   */
  static readonly forgetHooks: ((mats: ReadonlySet<THREE.Material>) => THREE.Material[])[] = [];
  /**
   * Per-model surface changes other modules own, run once on each newly converted material (a skin model's own glass:
   * render/skins/kit/modelSurface.ts). `glow` says the material is a converted `_glow` slot.
   */
  static readonly convertHooks: ((mat: THREE.Material, modelName: string, glow: boolean) => void)[] = [];

  /** The HQ road tiles' brightening over the painted cobble (they must read against the turf). */
  static readonly RoadTileGain: readonly [number, number, number] = [1.22, 1.14, 1.0];
  /** The worn road skin's multiplier on the road tiles (render/skins/roadSkin.ts; white = stock). */
  static readonly roadTint = new THREE.Color(1, 1, 1);
  /** The loaded road-tile materials (`g_path_*`, `g_ramp*`), so a road skin can re-tint them in place. */
  static readonly roadTiles = new Set<THREE.MeshStandardMaterial>();
  /** Wears a road tint on the tile road (the ground-mesh road reads its own uniform; roadSkin.ts sets both). */
  static setRoadTint(tint: THREE.Color): void {
    HqMaterials.roadTint.copy(tint);
    const [r, g, b] = HqMaterials.RoadTileGain;
    for (const m of HqMaterials.roadTiles) m.color.setRGB(r, g, b).multiply(tint);
  }

  /** Converts one source material for a mesh of model `modelName`; cached per source so clones share GPU state. */
  convert(source: THREE.MeshStandardMaterial, mesh: THREE.Mesh, modelName: string): THREE.Material {
    const glow = /_glow/i.test(mesh.name) || /_glow/i.test(source.name ?? "");
    const wind = !glow && !!mesh.geometry.getAttribute("color") && HqMaterials.Sways.test(modelName);
    const beam = glow && [mesh.name, mesh.parent?.name ?? ""].some((n) => /beam(_\d+)?$/i.test(n));   // the part node is named; its mesh child is `mesh_N`             // the chest's light shaft: additive, no depth write (CHEST_HANDOFF.md)
    const pool = /_pool$/.test(source.name ?? "");                 // the fortress courtyard's light pool (P3)
    if (pool) { mesh.castShadow = false; mesh.receiveShadow = false; }
    if (glow && HqMaterials.AdditiveGlow.test(modelName)) mesh.castShadow = false;   // added light casts no shadow
    const team = !glow && /_team$/.test(source.name ?? "");        // the fortress pennant: the emblem goes on its cloth
    if (team) FlagEmblem.projectUvs(mesh);
    const key = `${source.uuid}|${glow ? "glow" : ""}|${beam ? "beam" : ""}|${wind ? "wind" : ""}|${pool ? "pool" : ""}`;
    let m = this.cache.get(key);
    if (m) return m;
    if (pool) {
      m = HqMaterials.poolMaterial(source);
    } else if (beam) {
      m = HqMaterials.beamMaterial(mesh);
    } else if (glow) {
      m = HqMaterials.glowMaterial(source, modelName);
    } else {
      m = source;
      const ground = /^g_/.test(modelName);
      source.envMapIntensity = ground ? HqMaterials.envNow * HqMaterials.GroundEnvScale : HqMaterials.envNow;
      if (ground && source.normalMap) source.normalScale.set(0.7, 0.7);
      if (/^g_path_|^g_bridge|^g_ramp/.test(modelName)) source.color.setRGB(...HqMaterials.RoadTileGain);   // the road must read against the turf
      if (/^g_path_|^g_ramp/.test(modelName)) { HqMaterials.roadTiles.add(source); source.color.multiply(HqMaterials.roadTint); }
      HqMaterials.standard.push({ mat: source, ground });
      if (team) FlagEmblem.dressMaterial(source, HqMaterials.TeamColour);
      if (wind) HqMaterials.addWind(source);
      if (ground) HqMaterials.addGroundVariation(source, wind);
      if (!ground) HqMaterials.addMapHaze(source);
      const rim = HqMaterials.RimModels.test(modelName);
      // props thin out over the build plots while a tower is being placed (seeThrough.ts); towers fade whole instead
      HqMaterials.addLitExtras(source, rim, !ground && !rim);
      if (wind && HqMaterials.Foliage.test(modelName)) HqMaterials.addFoliageLight(source);
    }
    if (!pool && !beam) for (const hook of HqMaterials.convertHooks) hook(m, modelName, glow);
    this.cache.set(key, m);
    return m;
  }

  /**
   * Drops every reference this module keeps to `mats` (the look list, the conversion cache, twins, pooled flash
   * copies) and disposes the twins and flash copies, so a model the library unloads (AssetLibrary.release) can be
   * collected. The caller disposes `mats` themselves and their textures.
   */
  forget(mats: ReadonlySet<THREE.Material>): void {
    for (const [k, m] of this.cache) if (mats.has(m)) this.cache.delete(k);
    const all = new Set(mats);
    for (const m of mats) HqMaterials.roadTiles.delete(m as THREE.MeshStandardMaterial);
    for (const hook of HqMaterials.forgetHooks) for (const v of hook(mats)) { all.add(v); v.dispose(); }
    for (const m of mats) { const t = HqMaterials.twins.get(m); if (t) { all.add(t); t.dispose(); } }
    for (let i = HqMaterials.standard.length - 1; i >= 0; i--) if (all.has(HqMaterials.standard[i].mat)) HqMaterials.standard.splice(i, 1);
    for (const m of all) { for (const c of HqMaterials.flashPool.get(m) ?? []) c.dispose(); HqMaterials.flashPool.delete(m); }
  }

  // ---------------------------------------------------------------------------------------------------------------
  // Glow: the `_glow` slot as HDR light (lighting review F2, towers review F1)
  // ---------------------------------------------------------------------------------------------------------------

  /**
   * Every tower, base, chest, lamp and crystal GLB ships its `<name>_glow` material with emissive factor 1 and
   * `KHR_materials_emissive_strength` 3 (checked across all 73 glow slots in dist/hq). GLTFLoader maps that extension
   * onto `emissiveIntensity`. The slot used to be rebuilt as `MeshBasicMaterial({ color: 0xffffff })`, which threw the
   * ×3 away: glows peaked at 1.0, at or under the bloom threshold, so the only thing on screen that bloomed was sunlit
   * road. Now the glow's colour is `emissive × emissiveIntensity` (≈3), or, for the elemental towers, a per-archetype
   * HDR colour (below) — the emissive map is the same painted atlas as the albedo, so the tint multiplies a texel that
   * already carries the element's hue.
   *
   * `toneMapped` is left at its default (true). The old `false` did nothing inside the composer (the final pass tone-maps
   * the whole buffer), and without post — low tiers — an HDR colour that skips tone mapping just clips to flat white.
   * Tone-mapped, a ×3 glow still reads as the brightest thing in the frame but keeps its hue.
   */
  static readonly GlowTints: readonly [RegExp, readonly [number, number, number]][] = [
    [/^tower_frost_/, [1.3, 1.8, 2.4]],
    [/^tower_lightning_/, [1.6, 1.9, 2.6]],        // cyan-white today; ART_WOW_PASS asks Kevin whether lightning goes violet-white
    [/^tower_fire_/, [2.4, 1.4, 0.6]],
    [/^tower_poison_/, [1.2, 2.2, 0.8]],
    [/^tower_beacon_/, [2.4, 2.1, 1.3]],
    // TOWERS_V2 (art hand-off 2026-10-04): the Arcane Spire's rose-magenta crystal, the Hex Totem's violet eyes and ward
    [/^tower_arcane_/, [2.4, 1.2, 2.2]],
    [/^tower_hex_/, [1.8, 1.1, 2.6]],
  ];

  /**
   * A premium tower skin's own lights (`tower_<id>_t<n>__<skin>`: its lanterns, windows, garnets, braziers) ship in a
   * second glow slot `<model>_skin_glow` (art/towerkit/build_tower_skin.py) so they are NOT multiplied by the tower's
   * element tint above: a frost tower's red lanterns would turn pink, a fire tower's garnets orange. The model's element
   * parts stay in `<model>_glow` and keep the tint; stock towers have no such slot and are unchanged.
   */
  static readonly SkinGlow = /_skin_glow$/;

  /** The HDR tint a glow slot wears: the element's for a tower's `_glow`, none (as baked) for a skin's own glow. */
  static glowTintFor(modelName: string, materialName: string): readonly [number, number, number] | null {
    if (HqMaterials.SkinGlow.test(materialName)) return null;
    return HqMaterials.GlowTints.find(([re]) => re.test(modelName))?.[1] ?? null;
  }

  /** The player's colour: the fortress pennant (`<base>_team` material) wears it, round the emblem (flagEmblem.ts). */
  static readonly TeamColour = new THREE.Color(0.18, 0.43, 0.95);
  /** The courtyard pool's HDR amber (towers review F6b: "forge glow at HDR 2.0 plus an amber additive ground quad"). */
  static readonly PoolColour = new THREE.Color(2.0, 1.25, 0.55);

  /**
   * The fortress courtyard's warm light pool: an additive radial quad (its emissive map is the falloff) that breathes
   * with the other glows and flares with the fortress's charge (it is a shared glow, so `flashGlow` reaches it). One
   * draw, no light: the dark courtyard pit reads as lamplit without a PointLight.
   */
  static poolMaterial(source: THREE.MeshStandardMaterial): THREE.MeshBasicMaterial {
    const m = new THREE.MeshBasicMaterial({ color: HqMaterials.PoolColour, map: source.emissiveMap ?? source.map ?? null, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false });
    m.name = source.name;
    m.userData.tdGlowPulse = 1;
    HqMaterials.hookGlow(m, { value: 1 });
    HqMaterials.glowShared.add(m);
    return m;
  }

  /**
   * Shared glow uniforms. `glowGain` scales every glow (1 = as authored; a hero moment can dip or boost the world's
   * lanterns at once). `glowPulseAmp` scales the breathing: 1 = the 0.8..1.4 swing, 2 = the "boss dusk" doubling.
   */
  static readonly glowGain = { value: 1 };
  static readonly glowPulseAmp = { value: 1 };
  /** Models whose glow must not breathe: projectiles move, and a phase tied to position would flicker as they fly. */
  static readonly SteadyGlow = /^proj_/;

  /** Every shared (non-flash) glow material, so `flashGlow` can recognise one on a mesh. */
  private static readonly glowShared = new WeakSet<THREE.Material>();

  /**
   * Glows drawn as ADDED light instead of an opaque unlit surface: the spawn portal's swirl, ground ring and rune
   * panels. Those GLBs share one 256² auto-packed atlas with black gutters between islands, and their `_glow` slots
   * reuse it as the emissive map — opaque, the gutters showed as black blotches on the ground ring, mip levels bled
   * black into the swirl's edges, and the swirl card read as a flat lit board (a hard blue rectangle in boss dusk).
   * Added, black adds nothing: the gutters and the card's dark corners vanish and only the painted light shows.
   * Towers, bases, units and props keep the opaque glow — theirs are lit panels ON a model, where adding would let
   * the geometry behind show through.
   */
  static readonly AdditiveGlow = /^spawn_portal/;
  /**
   * An additive glow's strength in place of the authored ×3 emissive strength, and the cap on each layer's brightest
   * channel (linear, after the breathing pulse; the cap keeps the hue). Low on purpose: the swirl is several stacked,
   * double-sided sheets and every layer the eye looks through adds — at ×1.5 the stack summed to a flat white board,
   * at ×3 opaque it was a blown-out lavender card. At these values (checked in a render against the old material, day
   * and boss dusk) the swirl reads as a blue-violet vortex with its painted structure, and the stacked core still
   * crosses the bloom threshold.
   */
  static readonly AdditiveGlowGain = 0.35;
  static readonly AdditiveGlowCap = 0.5;
  /** "No cap" for the opaque glows: far above any tint × pulse × flash they reach. */
  private static readonly NoGlowCap = 1e4;

  private static glowMaterial(source: THREE.MeshStandardMaterial, modelName: string): THREE.MeshBasicMaterial {
    const additive = HqMaterials.AdditiveGlow.test(modelName);
    const tint = HqMaterials.glowTintFor(modelName, source.name ?? "");
    const colour = new THREE.Color();
    if (tint) colour.setRGB(tint[0], tint[1], tint[2]);
    else {
      const e = source.emissive && (source.emissive.r + source.emissive.g + source.emissive.b) > 0 ? source.emissive : new THREE.Color(1, 1, 1);
      colour.copy(e).multiplyScalar(additive ? HqMaterials.AdditiveGlowGain : source.emissiveIntensity > 0 ? source.emissiveIntensity : 3);
    }
    let map = source.map ?? source.emissiveMap ?? null;
    if (map && additive) {
      // Level 0 only: the atlas islands are magnified on screen anyway, and a mip chain averages each island with its
      // black gutter and its neighbours. A copy, so the albedo slot that shares the image keeps its mips.
      map = map.clone();
      map.generateMipmaps = false;
      map.minFilter = THREE.LinearFilter;
      map.needsUpdate = true;
    }
    // `side` comes from the source: every glow slot is authored double-sided, and the portal's rune panels and swirl
    // card are single sheets that vanished from behind on MeshBasicMaterial's default front side.
    const m = new THREE.MeshBasicMaterial({ color: colour, map, side: source.side });
    if (additive) { m.transparent = true; m.blending = THREE.AdditiveBlending; m.depthWrite = false; }
    m.name = source.name;
    m.userData.tdGlowPulse = HqMaterials.SteadyGlow.test(modelName) ? 0 : 1;
    m.userData.tdGlowCap = additive ? HqMaterials.AdditiveGlowCap : HqMaterials.NoGlowCap;
    HqMaterials.hookGlow(m, { value: 1 });
    HqMaterials.glowShared.add(m);
    return m;
  }

  /**
   * The breathing pulse and the per-material flash multiplier, patched into MeshBasicMaterial.
   *
   * Pulse: `0.8 + 0.6 * (0.5 + 0.5 * sin(t * 2.1 + phase))` — a 3 s breath between 0.8× and 1.4× (towers review F1).
   * The phase comes from the object's world position (the model matrix's translation, times the instance matrix for
   * instanced meshes) so neighbouring towers do not pulse in lockstep. It is a smooth function of position, not a hash:
   * enemies and bosses carry glows and walk, and a hash of a moving position flickers every frame. Static-batched
   * scenery has an identity model matrix; there the vertex's own world position stands in, which gives a slow shimmer
   * across a big crystal instead of a single breath — fine for scenery.
   *
   * Flash: `uGlowFlash` multiplies the result; it is a PER-MATERIAL uniform object (one GL program is shared by every
   * glow — the cache key is fixed — but three.js keeps uniforms per material), which is how `flashGlow` drives one
   * tower without touching the others.
   */
  private static hookGlow(m: THREE.MeshBasicMaterial, flash: { value: number }): void {
    const pulse = { value: m.userData.tdGlowPulse as number };
    const cap = { value: (m.userData.tdGlowCap as number | undefined) ?? HqMaterials.NoGlowCap };
    m.userData.tdGlowFlash = flash;
    m.onBeforeCompile = (sh) => {
      sh.uniforms.uGlowTime = HqMaterials.time;
      sh.uniforms.uGlowGain = HqMaterials.glowGain;
      sh.uniforms.uGlowAmp = HqMaterials.glowPulseAmp;
      sh.uniforms.uGlowPulse = pulse;
      sh.uniforms.uGlowFlash = flash;
      sh.uniforms.uGlowCap = cap;
      sh.vertexShader = sh.vertexShader
        .replace("#include <common>", "#include <common>\nvarying float vGlowPhase;")
        .replace("#include <project_vertex>", `#include <project_vertex>
  {
    vec4 gA = vec4(0.0, 0.0, 0.0, 1.0);
    #ifdef USE_INSTANCING
      gA = instanceMatrix * gA;
    #endif
    gA = modelMatrix * gA;
    if (dot(gA.xz, gA.xz) < 1e-6) gA = modelMatrix * vec4(transformed, 1.0);
    vGlowPhase = gA.x * 1.7 + gA.z * 2.3 + gA.y * 0.9;
  }`);
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", "#include <common>\nvarying float vGlowPhase;\nuniform float uGlowTime, uGlowGain, uGlowAmp, uGlowPulse, uGlowFlash, uGlowCap;")
        .replace("#include <opaque_fragment>", `{
    float breath = 1.1 + 0.3 * uGlowAmp * sin(uGlowTime * 2.1 + vGlowPhase);
    outgoingLight *= uGlowGain * uGlowFlash * mix(1.0, max(breath, 0.0), uGlowPulse);
    float gPeak = max(max(outgoingLight.r, outgoingLight.g), outgoingLight.b);
    outgoingLight *= min(1.0, uGlowCap / max(gPeak, 1e-4));      // hue-preserving cap (additive glows)
  }
  #include <opaque_fragment>`);
    };
    m.customProgramCacheKey = () => "td-glow";
    m.needsUpdate = true;
  }

  /** A glow flash in flight: which mesh (and material slot) wears a flash copy, and its envelope. */
  private static readonly flashes: { mesh: THREE.Mesh; slot: number; shared: THREE.MeshBasicMaterial; copy: THREE.MeshBasicMaterial; t: number; dur: number; peak: number }[] = [];
  /** Flash copies not in use, per shared material: pooled so a tower firing twice a second allocates nothing. */
  private static readonly flashPool = new Map<THREE.Material, THREE.MeshBasicMaterial[]>();

  /**
   * Flashes every glow under `root` to `strength`× for `seconds`, decaying back to 1 (the towers view calls this on
   * each shot: `HqMaterials.flashGlow(view.root, 2.5, 0.12)`; the base strike can hold 4× through its charge by
   * calling it every frame with a short duration). Re-flashing a mesh that is still flashing restarts it.
   *
   * How: the mesh's shared glow material is swapped for a pooled copy with its own `uGlowFlash` uniform (same GL
   * program, so no compile), and swapped back when the flash ends. Nothing else about the mesh changes. Cost: one
   * extra material bind per flashing mesh while it flashes.
   */
  static flashGlow(root: THREE.Object3D, strength = 2.5, seconds = 0.12): void {
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      mats.forEach((mat, slot) => {
        const live = HqMaterials.flashes.find((f) => f.mesh === mesh && f.slot === slot);
        if (live) { live.t = 0; live.dur = seconds; live.peak = strength; return; }
        if (!HqMaterials.glowShared.has(mat)) return;
        const shared = mat as THREE.MeshBasicMaterial;
        const copy = HqMaterials.flashPool.get(shared)?.pop() ?? HqMaterials.flashCopy(shared);
        (copy.userData.tdGlowFlash as { value: number }).value = strength;
        HqMaterials.setSlot(mesh, slot, copy);
        HqMaterials.flashes.push({ mesh, slot, shared, copy, t: 0, dur: Math.max(1e-3, seconds), peak: strength });
      });
    });
  }

  private static flashCopy(shared: THREE.MeshBasicMaterial): THREE.MeshBasicMaterial {
    const c = shared.clone();                       // Material.copy drops onBeforeCompile; hookGlow re-adds it
    c.userData = { tdGlowPulse: shared.userData.tdGlowPulse, tdGlowCap: shared.userData.tdGlowCap };
    HqMaterials.hookGlow(c, { value: 1 });
    return c;
  }

  private static setSlot(mesh: THREE.Mesh, slot: number, m: THREE.Material): void {
    if (Array.isArray(mesh.material)) mesh.material[slot] = m; else mesh.material = m;
  }

  /** Decays the glow flashes; SceneRig.update calls it every frame. */
  static updateGlow(dt: number): void {
    const list = HqMaterials.flashes;
    for (let i = list.length - 1; i >= 0; i--) {
      const f = list[i];
      f.t += dt;
      const k = Math.min(1, f.t / f.dur);
      (f.copy.userData.tdGlowFlash as { value: number }).value = 1 + (f.peak - 1) * (1 - k) * (1 - k);   // ease-out back to 1
      if (k < 1) continue;
      HqMaterials.setSlot(f.mesh, f.slot, f.shared);
      let pool = HqMaterials.flashPool.get(f.shared);
      if (!pool) { pool = []; HqMaterials.flashPool.set(f.shared, pool); }
      pool.push(f.copy);
      list[i] = list[list.length - 1]; list.pop();
    }
  }

  // ---------------------------------------------------------------------------------------------------------------
  // Lit extras: AO on direct light, cloud shadows, strike-light slot, tower rim
  // ---------------------------------------------------------------------------------------------------------------

  /** Models that get the warm rim (towers review / lighting F8: towers only; units get theirs from the unit shader). */
  static readonly RimModels = /^(tower_|base_)/;
  /** Rim colour × strength (linear), shared: `0xffe8c8 × 0.15`. Set to black to switch the rim off everywhere. */
  static readonly rim = { value: new THREE.Color(0xffe8c8).multiplyScalar(0.15) };
  /** The sun's direction in VIEW space, for the rim's sun-side bias; SceneRig keeps it current. */
  static readonly rimSunView = { value: new THREE.Vector3(0, 1, 0) };
  /**
   * How much of the baked AO reaches direct light (lighting F4a). three's `aoMap` only darkens INDIRECT light, and in
   * this rig most of a sunlit pixel is direct (sun 2.8 against hemi 0.95), so the AO baked into every GLB barely
   * showed and tower feet floated on the grass on every tier without GTAO — i.e. every phone.
   */
  static readonly aoDirect = { value: 0.55 };
  /**
   * Foliage light (terrain F5), kept small: x = wrap (how far the sun wraps past the terminator, 0 = plain Lambert),
   * y = back-light (sun behind the crown, seen through the leaves). The lobe canopies' transferred normals give the
   * soft gradient; this only lifts the far side off black and adds a rim of translucency.
   */
  static readonly foliage = { value: new THREE.Vector2(0.45, 0.3) };

  /**
   * Patches one lit HQ material with the shared lighting extras. Everything is appended after `#include <aomap_fragment>`
   * — `ambientOcclusion` is declared inside that chunk (under USE_AOMAP), so the direct-light AO must come after it,
   * and the direct terms are still separate there (they are summed right after, into totalDiffuse).
   */
  private static addLitExtras(mat: THREE.MeshStandardMaterial, rim: boolean, see = false): void {
    const prev = mat.onBeforeCompile;
    mat.onBeforeCompile = (sh, r) => {
      prev?.call(mat, sh, r);
      CloudShadows.inject(sh);                                  // brings the world-position varying vCloudWP
      StrikeLight.inject(sh, "vCloudWP");
      sh.uniforms.uAoDirect = HqMaterials.aoDirect;
      if (rim) { sh.uniforms.uRim = HqMaterials.rim; sh.uniforms.uRimSun = HqMaterials.rimSunView; }
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", `#include <common>\nuniform float uAoDirect;${rim ? "\nuniform vec3 uRim;\nuniform vec3 uRimSun;" : ""}`)
        .replace("#include <aomap_fragment>", `#include <aomap_fragment>
  #ifdef USE_AOMAP
    reflectedLight.directDiffuse *= mix(1.0, ambientOcclusion, uAoDirect);
  #endif${rim ? `
  {
    float rimNV = saturate(dot(normal, geometryViewDir));
    totalEmissiveRadiance += uRim * pow(max(1e-6, 1.0 - rimNV), 3.0) * (0.4 + 0.6 * saturate(dot(normal, uRimSun)));
  }` : ""}`);
      if (see) SeeThrough.inject(sh);
      VfxLights.inject(sh);                                     // VFX light slots (vfxLights.ts): last, after every other replace
    };
    const old = mat.customProgramCacheKey?.() ?? "";
    mat.customProgramCacheKey = () => `${old}-lit${rim ? "-rim" : ""}${see ? "-see" : ""}-vfxl`;
    mat.needsUpdate = true;
  }

  /**
   * Wrap lighting + a cheap back-light on leaves (ART_WOW_PASS Phase 3 §4). The sun's Lambert term is replaced by a
   * wrapped one — `saturate((N·L + w) / (1 + w))` — by ADDING the difference, so shadows, the cloud shadows (applied
   * after) and the rest of the lighting stay as they are; the difference is not shadowed, which is the point: the
   * shaded side of a crown reads as lit-through foliage rather than the navy it went before. Only the first
   * directional light (the sun). ~10 ALU on foliage pixels; no new sampler, uniform or light.
   */
  private static addFoliageLight(mat: THREE.MeshStandardMaterial): void {
    const prev = mat.onBeforeCompile;
    mat.onBeforeCompile = (sh, r) => {
      prev?.call(mat, sh, r);
      sh.uniforms.uFoliage = HqMaterials.foliage;
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", "#include <common>\nuniform vec2 uFoliage;")
        .replace("#include <lights_fragment_end>", `#include <lights_fragment_end>
  #if NUM_DIR_LIGHTS > 0 && defined( USE_COLOR )
  {
    float fol = vColor.b;
    vec3 sunL = directionalLights[ 0 ].direction;
    vec3 sunC = directionalLights[ 0 ].color;
    float ndl = dot( normal, sunL );
    float wrapT = saturate( ( ndl + uFoliage.x ) / ( 1.0 + uFoliage.x ) ) - saturate( ndl );
    float backT = pow( saturate( dot( geometryViewDir, -sunL ) ), 4.0 ) * uFoliage.y;
    reflectedLight.directDiffuse += fol * ( wrapT + backT ) * sunC * BRDF_Lambert( material.diffuseColor );
  }
  #endif`);
    };
    const old = mat.customProgramCacheKey?.() ?? "";
    mat.customProgramCacheKey = () => `${old}-foliage`;
    mat.needsUpdate = true;
  }

  /**
   * The reward chest's light shaft (CHEST_HANDOFF.md "One note on the light shaft"): additive, unlit, values above 1
   * feed the bloom pass. The `beam` part is not one lathe: the baked GLB is a narrow cone up the middle plus a fan of
   * flat, hard-edged ray blades (and a few specks), all in the `_glow` slot with no texture and no alpha. The first cut
   * of this shader only softened by facing — right for a cone, a no-op for a blade turned to the camera — so every
   * blade drew as a flat pale slab, the cone as a solid white spike, and together over the lid's inner face they read
   * as the "pale translucent square at the chest mouth". Now the light falls off with distance from the part's pivot
   * (the chest mouth: bright there, gone at the blade tips), stays the chest's rank colour except for a small hot core
   * at the mouth, and the ChestStage sets `uColor` to the rarity colour.
   */
  static beamMaterial(mesh: THREE.Mesh): THREE.ShaderMaterial {
    const geometry = mesh.geometry;
    geometry.computeBoundingBox();
    const bb = geometry.boundingBox!;
    // The pivot of the `beam` part in the mesh's own (meshopt-quantized) space: the mesh node carries the dequantizing
    // offset and scale, the part node above it is what the clip scales from the mouth.
    mesh.updateMatrix();
    const pivot = new THREE.Vector3().applyMatrix4(mesh.matrix.clone().invert());
    const pos = geometry.getAttribute("position");
    const v = new THREE.Vector3();
    let rMax = 0;
    for (let i = 0; i < pos.count; i++) rMax = Math.max(rMax, v.fromBufferAttribute(pos, i).distanceTo(pivot));
    return new THREE.ShaderMaterial({
      uniforms: {
        uTime: HqMaterials.beamTime, uYMin: { value: bb.min.y }, uYMax: { value: bb.max.y },
        uPivot: { value: pivot }, uRMax: { value: Math.max(1e-3, rMax) }, uColor: { value: new THREE.Color(1.0, 0.93, 0.72) },
      },
      vertexShader: `
        varying float vT; varying float vAngle; varying float vFacing; varying float vR;
        uniform float uYMin, uYMax, uRMax; uniform vec3 uPivot;
        void main() {
          vT = clamp((position.y - uYMin) / max(0.001, uYMax - uYMin), 0.0, 1.0);
          vec3 d = position - uPivot;
          vR = clamp(length(d) / uRMax, 0.0, 1.0);
          vAngle = atan(d.z, d.x);
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          vec3 n = normalMatrix * normal, e = -mv.xyz;
          vFacing = abs(dot(n, e)) / max(1e-6, length(n) * length(e));
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `
        varying float vT; varying float vAngle; varying float vFacing; varying float vR;
        uniform float uTime; uniform vec3 uColor;
        float hash(float n) { return fract(sin(n) * 43758.5453); }
        float noise(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
          float a = hash(i.x + i.y * 57.0), b = hash(i.x + 1.0 + i.y * 57.0), c = hash(i.x + (i.y + 1.0) * 57.0), d = hash(i.x + 1.0 + (i.y + 1.0) * 57.0);
          return mix(mix(a, b, f.x), mix(c, d, f.x), f.y); }
        void main() {
          float edge = pow(clamp(vFacing, 1e-6, 1.0), 1.1);                              // a cone's silhouette goes soft
          float fall = pow(max(1e-6, 1.0 - vR), 2.4);                             // bright at the mouth, nothing at the blade tips
          float base = smoothstep(0.0, 0.08, vT);                      // not below the chest floor
          float rays = 0.55 + 0.45 * noise(vec2(vAngle * 2.2, vR * 6.0 - uTime * 1.6));
          float flicker = 0.9 + 0.1 * sin(uTime * 9.0 + vAngle * 3.0);
          float a = edge * fall * base * rays * flicker * 0.7;
          // the rank's colour, kept under the grade's roll-off so it keeps its hue; only the very core runs toward white
          vec3 col = mix(uColor, vec3(1.0), 0.15 * fall * fall * fall) * 1.35;
          gl_FragColor = vec4(col * a, a);
        }`,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, toneMapped: false,
    });
  }

  /**
   * Macro colour variation for the grass and path tiles: a low-frequency world-space noise pulls each spot toward a
   * warm straw tint or a cool deep green, so a field of identical tiles stops reading as a repeating swatch.
   */
  private static addGroundVariation(mat: THREE.MeshStandardMaterial, alsoWind = false): void {
    const prev = mat.onBeforeCompile;
    mat.onBeforeCompile = (sh, r) => {
      if (alsoWind) prev.call(mat, sh, r);
      sh.uniforms.uGroundVar = HqMaterials.groundVar; sh.uniforms.uGroundWarm = HqMaterials.groundWarm; sh.uniforms.uGroundCool = HqMaterials.groundCool;
      sh.vertexShader = sh.vertexShader
        .replace("#include <common>", "#include <common>\nvarying vec3 vWorldP;")
        .replace("#include <worldpos_vertex>", "#include <worldpos_vertex>\nvWorldP = (modelMatrix * vec4(transformed, 1.0)).xyz;");
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", `#include <common>
          varying vec3 vWorldP; uniform float uGroundVar; uniform vec3 uGroundWarm, uGroundCool;
          float gvHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
          float gvNoise(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
            return mix(mix(gvHash(i), gvHash(i + vec2(1, 0)), f.x), mix(gvHash(i + vec2(0, 1)), gvHash(i + vec2(1, 1)), f.x), f.y); }`)
        .replace("#include <color_fragment>", `#include <color_fragment>
          if (uGroundVar > 0.0) {
            float n = gvNoise(vWorldP.xz * 0.23) * 0.6 + gvNoise(vWorldP.xz * 0.61 + 7.3) * 0.4;
            float lum = dot(diffuseColor.rgb, vec3(0.3, 0.59, 0.11));
            vec3 warm = uGroundWarm * (lum / max(0.05, dot(uGroundWarm, vec3(0.3, 0.59, 0.11))));
            vec3 cool = uGroundCool * (lum / max(0.05, dot(uGroundCool, vec3(0.3, 0.59, 0.11))));
            vec3 target = n > 0.5 ? mix(diffuseColor.rgb, warm, (n - 0.5) * 2.0) : mix(diffuseColor.rgb, cool, (0.5 - n) * 2.0);
            diffuseColor.rgb = mix(diffuseColor.rgb, target, uGroundVar);
          }`);
    };
    mat.customProgramCacheKey = () => (alsoWind ? "td-wind-groundvar" : "td-groundvar");
    mat.needsUpdate = true;
  }

  /**
   * The same map-edge haze the ground applies, on every prop and building.
   *
   * The ground dissolves into haze past the playable rectangle; without this the trees and rocks standing on that
   * ground stay perfectly crisp and appear to float above the fog. Shares its uniform objects with every material it
   * touches, so `setMapHaze` updates all of them at once.
   */
  static readonly hazeColor = { value: new THREE.Color(0xa9c2de) };
  static readonly hazeBounds = { value: new THREE.Vector4(-0.5, -0.5, 1, 1) };
  static readonly hazeRange = { value: new THREE.Vector2(4, 12) };

  /** Tells every prop material where the playable rectangle is, and what the haze looks like. */
  static setMapHaze(minX: number, minZ: number, width: number, height: number, colour: number, start: number, full: number): void {
    HqMaterials.hazeBounds.value.set(minX, minZ, width, height);
    HqMaterials.hazeColor.value.set(colour);
    HqMaterials.hazeRange.value.set(start, full);
  }

  private static addMapHaze(mat: THREE.MeshStandardMaterial): void {
    const prev = mat.onBeforeCompile;
    mat.onBeforeCompile = (sh, r) => {
      prev?.call(mat, sh, r);
      sh.uniforms.uHazeCol = HqMaterials.hazeColor;
      sh.uniforms.uHazeBox = HqMaterials.hazeBounds;
      sh.uniforms.uHazeRng = HqMaterials.hazeRange;
      sh.vertexShader = sh.vertexShader
        .replace("#include <common>", "#include <common>\nvarying vec3 vHazeWP;")
        // Instanced meshes (the detail scatter, the card clumps, the gorge scree) must go through instanceMatrix: without
        // it every instance hazes at its model-space position — for a quantized mesh that is thousands of units out,
        // so the whole scatter drew as flat haze-coloured flecks (lead capture 2026-09-22).
        .replace("#include <worldpos_vertex>", "#include <worldpos_vertex>\n  #ifdef USE_INSTANCING\n  vHazeWP = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;\n  #else\n  vHazeWP = (modelMatrix * vec4(transformed, 1.0)).xyz;\n  #endif");
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", "#include <common>\nvarying vec3 vHazeWP;\nuniform vec3 uHazeCol;\nuniform vec4 uHazeBox;\nuniform vec2 uHazeRng;")
        .replace("#include <opaque_fragment>", `#include <opaque_fragment>
          vec2 hzLo = uHazeBox.xy, hzHi = uHazeBox.xy + uHazeBox.zw;
          vec2 hzD = max(hzLo - vHazeWP.xz, vHazeWP.xz - hzHi);
          float hz = smoothstep(uHazeRng.x, uHazeRng.y, length(max(hzD, vec2(0.0))));
          // Match the GROUND's curve, or the two disagree and the trees stand crisp over ground that has already
          // dissolved. The ground shader mixes the haze into diffuseColor and then mixes it again over the lit
          // result at 0.85, so at half distance it is 71% hazed where a single mix here would be 50%. Same two
          // stages, same numbers.
          hz = 1.0 - (1.0 - hz) * (1.0 - hz * 0.85);
          gl_FragColor.rgb = mix(gl_FragColor.rgb, uHazeCol, hz);`);
    };
    const old = mat.customProgramCacheKey?.() ?? "";
    mat.customProgramCacheKey = () => `${old}-haze`;
    mat.needsUpdate = true;
  }

  /**
   * One gust burst at a time, for the wind shader: centre x/z, radius, and the `time` it set out (far in the past =
   * none). A boss arriving shakes the trees around the portal; the trees are baked static scenery (foliage.ts), so
   * the shake is the shader's, not a clip.
   */
  static readonly burst = { value: new THREE.Vector4(0, 0, 1, -1e4) };
  static readonly burstStrength = { value: 1 };
  static gust(x: number, z: number, radius: number, strength: number): void {
    HqMaterials.burst.value.set(x, z, radius, HqMaterials.time.value);
    HqMaterials.burstStrength.value = strength;
  }

  /** Per-vertex sway from `COLOR_0` (r = wind weight root→tip, g = height fraction); the texture stays unmodulated. */
  private static addWind(mat: THREE.MeshStandardMaterial): void {
    mat.vertexColors = true;
    mat.onBeforeCompile = (sh) => {
      sh.uniforms.uTime = HqMaterials.time;
      sh.uniforms.uBurst = HqMaterials.burst;
      sh.uniforms.uBurstK = HqMaterials.burstStrength;
      sh.vertexShader = sh.vertexShader
        .replace("#include <common>", "#include <common>\nuniform float uTime;\nuniform vec4 uBurst;\nuniform float uBurstK;")
        .replace("#include <begin_vertex>", `#include <begin_vertex>
        #ifdef USE_COLOR
          float w = vColor.r;
          // the scattered detail is an InstancedMesh: without the instance matrix every tuft in the field shares one
          // phase and the whole meadow sways as a single object
          #ifdef USE_INSTANCING
            vec4 wp = modelMatrix * instanceMatrix * vec4(position, 1.0);
          #elif defined( USE_SKINNING )
            // a rigid rig (rigidRig.ts): the part's own node places the vertex, exactly as the unmerged part's modelMatrix did
            // (atlasBindMatrix: the bind matrix without the bone-atlas slot it carries, boneAtlas.ts)
            vec4 wp = modelMatrix * bindMatrixInverse * boneMatX * atlasBindMatrix() * vec4(position, 1.0);
          #else
            vec4 wp = modelMatrix * vec4(position, 1.0);
          #endif
          float ph = wp.x * 0.9 + wp.z * 0.7 + vColor.g * 2.0;
          float gust = 0.3 + 0.9 * smoothstep(0.55, 1.0, sin(dot(wp.xz, vec2(0.8, 0.6)) * 0.8 - uTime * 1.6)) * smoothstep(0.3, 0.75, 0.5 + 0.5 * sin(dot(wp.xz, vec2(-0.6, 0.8)) * 0.19 + uTime * 0.11) * cos(dot(wp.xz, vec2(0.8, 0.6)) * 0.07 - uTime * 0.05));   // GroundMesh.windGlsl's band (ground agent hook)
          transformed.x += w * (0.035 + 0.05 * gust) * sin(uTime * 2.1 + ph);
          transformed.z += w * (0.025 + 0.03 * gust) * sin(uTime * 1.7 + ph * 1.3);
          transformed.y -= w * w * 0.01 * (1.0 + sin(uTime * 2.1 + ph));
          // a gust burst (HqMaterials.gust): a decaying shake, strongest at its centre, gone past its radius
          float bt = uTime - uBurst.w;
          if (bt >= 0.0 && bt < 3.0) {
            float bk = uBurstK * exp(-bt * 1.4) * (1.0 - smoothstep(0.0, uBurst.z, distance(wp.xz, uBurst.xy)));
            transformed.x += w * 0.09 * bk * sin(bt * 7.0 + ph);
            transformed.z += w * 0.06 * bk * sin(bt * 5.5 + ph * 1.3);
          }
        #endif`);
      sh.fragmentShader = sh.fragmentShader.replace("#include <color_fragment>", "");
    };
    mat.customProgramCacheKey = () => "td-wind";
    mat.needsUpdate = true;
  }
}
